import React, { useMemo, useContext } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import numeral from 'numeral';
import { Tooltip, Typography } from '@mui/material';

import AMRPredictionsBlock from '../AMRPredictions/AMRPredictionsBlock.jsx';
import resistanceGenesReference from './../AMRPredictions/resistanceGenesReference.js'
import { identifiedSpeciesIsVirus } from './utilities.js';
import MlstBlock from './MlstBlock.jsx';
import { parseMlstResults } from './mlstUtils.js';
import { IdentifiedSpeciesFiltersContext } from '../context/IdentifiedSpeciesFiltersContext.js';

import { useResistanceGenes, useAmrPredictions, useImputations, useMlst } from '../services/api/KeynomeAPIHooks.js';

function IdentifiedSpeciesRow(props) {
  const {
    isIllumina,
    identifiedSpecies,
    isDiagnosticView,
    totalMicrobialMegabases,
  } = props;

  const {
    showAST,
    showKIDCalls,
    showRnDModels,
    showImputedCalls,
    showResistanceGenes,
    showMLST,
    showSecondaryMetrics,
  } = useContext(IdentifiedSpeciesFiltersContext);

  let showKIDColumns = identifiedSpecies.attributes.species_call_raw_value !== null;

  const speciesCallRank = identifiedSpecies?.attributes.species_call_rank_by_kid_value ?? identifiedSpecies?.attributes.species_call_rank_by_reads;
  const speciesCallPositive = identifiedSpecies?.attributes.species_call_positive

  // Only load AMR predictions if Illumina sample or species call positive
  const { data: amrPredictions } = useAmrPredictions((speciesCallPositive || isIllumina) && identifiedSpecies?.id);
  const { data: mlstData } = useMlst(
    identifiedSpecies?.id,
    (speciesCallPositive || isIllumina) && showMLST
  );

  const mlstResults = useMemo(() => parseMlstResults(mlstData || []), [mlstData]);

  const highConfidencePredictions = amrPredictions?.filter(item => ['VH', 'H'].includes(item.attributes.model_performance_label));
  const mediumConfidencePredictions = amrPredictions?.filter(item => item.attributes.model_performance_label === 'M');

  const { data: resistanceGenes, isLoading: resistanceGenesLoading } = useResistanceGenes(
      identifiedSpecies?.id,
      {},
      // Only load resistance genes if AMR predictions are available
      !!amrPredictions && amrPredictions.length > 0
  );
  const { data: imputations } = useImputations(
      highConfidencePredictions?.map(prediction => prediction.id),
      // Only load imputations if in top 5 calls
      (speciesCallRank || 6) <= 5
  );

  const resistanceGeneHierarchyNodes = useMemo(() => {
      if (resistanceGenesLoading || !!!resistanceGenes) return [];

      const hierarchyNodes = resistanceGenes.reduce((acc, resGene) => {
          const node = resGene.attributes.hierarchy_node;
          if (!!node && !acc.has(node)) acc.add(node);
          return acc;
      }, new Set());
      return Array.from(hierarchyNodes);
  }, [resistanceGenes, resistanceGenesLoading]);

  const visiblePredictions = useMemo(() => {
      let predictions = []
      const predictedDrugs = new Set();

      if (!!highConfidencePredictions) {
          predictions = predictions.concat(highConfidencePredictions);
          highConfidencePredictions.forEach(prediction => predictedDrugs.add(prediction.attributes.model_drug));
      }

      if (showRnDModels && !!mediumConfidencePredictions) {
          predictions = predictions.concat(mediumConfidencePredictions);
          mediumConfidencePredictions.forEach(prediction => predictedDrugs.add(prediction.attributes.model_drug));
      }

      if (showImputedCalls && !!imputations) {
          const displayableImputations = imputations.filter(imputation => !!imputation && !predictedDrugs.has(imputation.attributes.drug));
          predictions = predictions.concat(displayableImputations);
          displayableImputations.forEach(imputation => predictedDrugs.add(imputation.attributes.drug));
      }

      if (showResistanceGenes && !!resistanceGeneHierarchyNodes) {
          // Create AMRPredictionRow for each resistance gene associated with
          // an antibotic not represented by an AMR prediction or imputation
          const resistanceGenePredictions = Object.values(resistanceGeneHierarchyNodes.reduce((acc, node) => {
              const nodeDetails = resistanceGenesReference[node];
              if (!nodeDetails) return acc;
              return nodeDetails.antibiotics
                .filter(drug => !predictedDrugs.has(drug))
                .reduce((innerAcc, drug) => {
                  if (!innerAcc[drug]) {
                      innerAcc[drug] = {drug: drug, resistanceGenes: []};
                  }
                  innerAcc[drug].resistanceGenes.push(nodeDetails.display_name);
                  return innerAcc
              }, acc);
          }, []));

          predictions = predictions.concat(resistanceGenePredictions)
      }

      predictions = predictions.filter(prediction => {
          const drug = prediction.attributes?.model_drug || prediction.attributes?.drug || prediction.drug
          return drug !== 'cefoxitin' && drug !== 'methicillin'
      })

      predictions.sort((a, b) => {
          const aDrug = a.attributes?.model_drug || a.attributes?.drug || a.drug
          const bDrug = b.attributes?.model_drug || b.attributes?.drug || b.drug
          return aDrug.localeCompare(bDrug)
        })

      return predictions;
      
  }, [showRnDModels, showImputedCalls, showResistanceGenes, highConfidencePredictions, mediumConfidencePredictions, imputations, resistanceGeneHierarchyNodes])

  const amrPredictionsById = useMemo(() => {
      return amrPredictions?.reduce((acc, prediction) => {
          acc[prediction.id] = prediction;
          return acc;
      }, {});
  }, [amrPredictions]);

  let amrPredictionsContent = null;
  if (showAST && (speciesCallPositive || isIllumina) && amrPredictions?.length > 0) {
      amrPredictionsContent = <AMRPredictionsBlock 
        isDiagnosticView={isDiagnosticView}
        visibleAmrPredictions={visiblePredictions} 
        qualityLimitations={identifiedSpecies.attributes.reasons_for_qc_failure} 
        amrPredictionsReference={amrPredictionsById}
        resistanceGenes={resistanceGeneHierarchyNodes}
      />
  }

  const speciesParts = identifiedSpecies.attributes.species.replace('[', '').split(' ');
  const speciesName = `${speciesParts[0][0]}. ${speciesParts.slice(1).join(' ')}`;
  const speciesNameContent = (identifiedSpecies.attributes.species_call_positive || (isIllumina && identifiedSpecies.attributes.reads_frac_agreeing_megabases >= 0.8))
    ? (<p style={{overflow: 'hidden', whiteSpace: 'nowrap', textOverflow:'ellipsis'}}><b>{speciesName}</b></p>)
    : (<p style={{ fontWeight: (showKIDCalls || isIllumina) ? null : 'bold', color: (showKIDCalls || isIllumina) ? 'gray' : null, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow:'ellipsis' }}>{speciesName}</p>);

  let positivityContent = null;
  if (!isDiagnosticView && showKIDCalls && showKIDColumns) {
    positivityContent = (
      identifiedSpecies.attributes.species_call_positive
      ? (<p style={{ color: 'green' }}><b>Positive </b></p>)
      : (
        <p style={{ color: 'gray' }}>
          Subthreshold
        </p>
      )
    );
  }


  const detailContentColor = (identifiedSpecies.attributes.species_call_positive || (!showKIDCalls && !isIllumina) || (isIllumina && identifiedSpecies.attributes.reads_frac_agreeing_megabases >= 0.8)) ? null : 'gray';

  let kidValueContent = null;
  if (!isDiagnosticView && identifiedSpecies.attributes.species_call_raw_value !== null) {
    kidValueContent = (
      <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small><b>
        {identifiedSpecies.attributes.species_call_raw_value < 0.001 ? '<0.001' : Math.min(identifiedSpecies.attributes.species_call_raw_value, 1.000).toFixed(3)}
          <br /> CID value
      </b></small></p>
    );
  }

  let readsToSpeciesMetric = null;
  if (!isDiagnosticView && !showKIDColumns) {
    readsToSpeciesMetric = {
      key: 'readsToSpecies',
      value: `${identifiedSpecies.attributes.reads_to_species ? identifiedSpecies.attributes.reads_to_species.toLocaleString() : '-'} read pairs`,
      label: 'matching organism',
    };
  }

  let fractionMegabasesToGenusContent = null;
  if (!isDiagnosticView && isIllumina && false) {
    fractionMegabasesToGenusContent = (
      <Grid item xs={12} sm={2}>
        <p style={{ color: detailContentColor, marginTop: '5px', marginBottom: '5px' }}><small>
          <b>{identifiedSpecies.attributes.reads_frac_agreeing_megabases ? (identifiedSpecies.attributes.reads_frac_agreeing_megabases * 100).toFixed(2) : '-'}%</b>
            <br />
          matching genus
        </small></p>
      </Grid>
    );
  }

  let megabasesToSpeciesMetric = null;
  if (!isDiagnosticView) {
      let megabasesToSpeciesValue = null
      let readsToSpeciesValue = null
      let percentMegabasesToSpeciesValue = null
      if (identifiedSpecies.attributes.reads_megabases_to_species) {
          megabasesToSpeciesValue = (
            identifiedSpecies.attributes.reads_megabases_to_species.toFixed(3)
          )

          percentMegabasesToSpeciesValue = totalMicrobialMegabases ? ((
            identifiedSpecies.attributes.reads_megabases_to_species / totalMicrobialMegabases
          ) * 100).toFixed(2) : null
      }

      if (identifiedSpecies.attributes.reads_to_species) {
        readsToSpeciesValue = identifiedSpecies.attributes.reads_to_species.toLocaleString()
      }

    megabasesToSpeciesMetric = {
      key: 'megabasesToSpecies',
      value: `${megabasesToSpeciesValue ?? '-'} MBs ${percentMegabasesToSpeciesValue ? `(${percentMegabasesToSpeciesValue}%)` : ''}${(readsToSpeciesValue && identifiedSpecies.attributes.species_call_raw_value !== null) ? ` / ${readsToSpeciesValue < 1000 ? readsToSpeciesValue : numeral(readsToSpeciesValue).format('0.0a')} ${isIllumina ? "read pairs" : "reads"}` : ''}`.trim(),
      label: 'matching organism',
    };
  }

  let coverageBreadthMetric = null;
  if (!isDiagnosticView && !isIllumina && identifiedSpecies.attributes.species_call_positive && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    coverageBreadthMetric = {
      key: 'coverageBreadth',
      value: `${identifiedSpecies.attributes.perc_nonzero_coverage ? (identifiedSpecies.attributes.perc_nonzero_coverage * 100).toFixed(2) : '-'}%`,
      label: 'reference coverage',
    };
  }

  let coverageDepthMetric = null;
  if (!isDiagnosticView && !isIllumina && identifiedSpecies.attributes.species_call_positive && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    coverageDepthMetric = {
      key: 'coverageDepth',
      value: `${identifiedSpecies.attributes.avg_depth ? (identifiedSpecies.attributes.avg_depth).toFixed(2) : '-'}`,
      label: 'average depth',
    };
  }

  let assemblyMegabasesToSpeciesMetric = null;
  if (!isDiagnosticView && isIllumina && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    assemblyMegabasesToSpeciesMetric = {
      key: 'assemblyMegabasesToSpecies',
      value: `${identifiedSpecies.attributes.assembly_megabases_to_species ? (identifiedSpecies.attributes.assembly_megabases_to_species).toLocaleString() : '-'} MBs from raw`,
      label: 'assembly matching organism',
    };
  }

  let assemblyContigsToSpeciesMetric = null;
  if (!isDiagnosticView && isIllumina && !identifiedSpeciesIsVirus(identifiedSpecies)) {
    assemblyContigsToSpeciesMetric = {
      key: 'assemblyContigsToSpecies',
      value: `${identifiedSpecies.attributes.contigs_to_species ? (identifiedSpecies.attributes.contigs_to_species).toLocaleString() : '-'} contigs from raw`,
      label: 'assembly matching organism',
    };
  }

  const secondaryMetrics = [
    readsToSpeciesMetric,
    megabasesToSpeciesMetric,
    fractionMegabasesToGenusContent,
    coverageBreadthMetric,
    coverageDepthMetric,
    assemblyMegabasesToSpeciesMetric,
    assemblyContigsToSpeciesMetric,
  ].filter(Boolean);
  const showMlstInPrimaryTier = !isDiagnosticView && showMLST && mlstResults.length > 0;
  const secondaryRowVisible = showSecondaryMetrics && secondaryMetrics.length > 0;
  const hasInnerBottomBorder = !!amrPredictionsContent || secondaryRowVisible;

  return (
    <Box sx={{ borderBottom: hasInnerBottomBorder ? 'none' : '#bdbdbd 2px solid' }}>
      <Grid container alignItems="center" sx={{ textAlign: 'center', pl: 3, pr: 1 }}>
        <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
          <Tooltip title={identifiedSpecies.attributes.species} placement="top">
            {speciesNameContent}
          </Tooltip>
        </Grid>
        {positivityContent && (
          <Grid item xs={12} sm={2}>
            {positivityContent}
          </Grid>
        )}
        {kidValueContent && (
          <Grid item xs={12} sm={3}>
            {kidValueContent}
          </Grid>
        )}
        {showMlstInPrimaryTier && (
          <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
            <MlstBlock mlstResults={mlstResults} />
          </Grid>
        )}
      </Grid>
      {secondaryRowVisible && (() => {
        const metricByKey = secondaryMetrics.reduce((acc, m) => { acc[m.key] = m; return acc; }, {});
        const primaryDataMetric = metricByKey.megabasesToSpecies || metricByKey.readsToSpecies;
        const splitLeftMetric = metricByKey.coverageBreadth || metricByKey.assemblyMegabasesToSpecies;
        const splitRightMetric = metricByKey.coverageDepth || metricByKey.assemblyContigsToSpecies;
        const renderMetric = (metric) => metric ? (
          <>
            <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {metric.value}
            </Typography>
            <Typography variant="caption" component="div">
              {metric.label}
            </Typography>
          </>
        ) : null;
        return (
          <Grid
            container
            alignItems="center"
            sx={{
              pl: 3,
              pr: 1,
              py: 0.5,
              color: detailContentColor,
              backgroundColor: '#f5f5f5',
              borderBottom: '#bdbdbd 2px solid',
            }}
          >
            <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
              {renderMetric(primaryDataMetric)}
            </Grid>
            <Grid item xs={12} sm={2} />
            <Grid item xs={12} sm={3}>
              <Grid container>
                <Grid item xs={6} sx={{ textAlign: 'center' }}>
                  {renderMetric(splitLeftMetric)}
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'center' }}>
                  {renderMetric(splitRightMetric)}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3} />
          </Grid>
        );
      })()}
      {amrPredictionsContent}
    </Box>
  );
}

export default IdentifiedSpeciesRow;
